import React, { FC, useEffect, useState } from 'react';

import { Breadcrumbs, Chip } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import { useMutation, useQuery } from 'react-apollo';
import { Link, useHistory } from 'react-router-dom';

import Button from 'Components-redesign/Button';
import ImageSelect from 'Components-redesign/Fields/ImageSelect';
import { Input } from 'Components-redesign/Fields/New/Input';
import Field from 'Components-redesign/Form/Field';
import Loader from 'Components-redesign/Loader';
import Modal from 'Components-redesign/Modal';
import ModalContentWrapper from 'Components-redesign/Modal/ModalContentWrapper';
import useModal from 'Components-redesign/Modal/useModal';
import Toggle from 'Components-redesign/Toggle';
import { Heading } from 'Components-redesign/Typography/Heading';
import { Paragraph } from 'Components-redesign/Typography/Paragraph';
import { useI18n } from 'Hooks';
import {
  AiQuery,
  AiQueryVariables,
  CreateRelease,
  DeleteRelease,
  GetRelease,
  UpdateRelease,
} from 'Scenes/Releases/api/__queries__';
import {
  AI_HELP,
  CREATE_RELEASE,
  DELETE_RELEASE,
  UPDATE_RELEASE,
} from 'Scenes/Releases/api/mutations';
import { GET_RELEASE } from 'Scenes/Releases/api/queries';
import { AI_CONTEXT } from 'Scenes/Releases/consts';
import { RELEASE_TYPE } from 'Scenes/Releases/types';
import { Box } from 'styles/box';

import { DeleteWrapper, ReleaseNoteWrapper, ScrollableBox, Wrapper } from '../../styles';

interface Note {
  titleLang: {
    en: string;
    se: string;
    es: string;
    no: string;
    tr: string;
    da: string;
    ar: string;
  };
  showToCustomers: boolean;
  imageUrls?: string[];
}

interface FormData {
  createdBy: string;
  titleLang: {
    en: string;
    se: string;
    es: string;
    no: string;
    tr: string;
    da: string;
    ar: string;
  };
  notes: Note[];
  productTags: string[];
}

interface Props {
  id?: string;
  username: string;
  refetch: () => void;
  onComplete: () => void;
}

/** Form for creating & editing a release with type RELEASE_NOTE */
const ReleaseNoteForm: FC<Props> = ({ id, username, refetch, onComplete }) => {
  const { t } = useI18n();

  const confirmDeleteModal = useModal();
  const history = useHistory();

  const [createRelease] = useMutation<CreateRelease>(CREATE_RELEASE);
  const [updateRelease] = useMutation<UpdateRelease>(UPDATE_RELEASE);
  const [deleteRelease] = useMutation<DeleteRelease>(DELETE_RELEASE);
  const [aiHelper] = useMutation<AiQuery, AiQueryVariables>(AI_HELP);

  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    createdBy: username,
    titleLang: {
      en: '',
      se: '',
      es: '',
      no: '',
      tr: '',
      da: '',
      ar: '',
    },
    notes: [
      {
        titleLang: {
          en: '',
          se: '',
          es: '',
          no: '',
          tr: '',
          da: '',
          ar: '',
        },
        showToCustomers: true,
        imageUrls: [],
      },
    ],
    productTags: [],
  });

  const releaseQuery = useQuery<GetRelease>(GET_RELEASE, {
    variables: {
      id,
    },
    skip: !id || id === 'new',
  });

  const release = releaseQuery.data?.getRelease;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (id && id !== 'new') {
      /** Update */
      await updateRelease({
        variables: {
          input: {
            id,
            type: RELEASE_TYPE.RELEASE_NOTES,
            titleLang: formData.titleLang,
            notes: formData.notes,
            productTags: formData.productTags,
          },
        },
      });
    } else {
      /** Create */
      try {
        await createRelease({
          variables: {
            input: {
              createdBy: username,
              type: RELEASE_TYPE.RELEASE_NOTES,
              titleLang: formData.titleLang,
              notes: formData.notes,
              productTags: formData.productTags,
              imageUrls: [],
            },
          },
        });
      } catch (error) {
        console.error('Error creating release:', error);
        setError(error);
      }
    }

    refetch();
    history.push('/releases');
    onComplete();
    setLoading(false);
  };

  const handleDelete = async () => {
    confirmDeleteModal.hide();

    setDeleteLoading(true);

    try {
      await deleteRelease({
        variables: {
          id,
        },
      });

      refetch();
      history.push('/releases');
    } catch (error) {
      console.error('Error deleting release:', error);
      setError(error);
    }

    setDeleteLoading(false);
  };

  useEffect(() => {
    if (release) {
      setFormData({
        titleLang: release.titleLang,
        notes: release.notes,
        productTags: release.productTags,
        createdBy: release.createdBy,
      });
    }
  }, [release]);

  const addNote = () => {
    const newNote = {
      titleLang: {
        en: '',
        se: '',
        es: '',
        no: '',
        tr: '',
        da: '',
        ar: '',
      },
      showToCustomers: true,
      imageUrls: [],
    };

    setFormData({
      ...formData,
      notes: [...formData.notes, newNote],
    });
  };

  return (
    <>
      <Wrapper loading={deleteLoading}>
        <Breadcrumbs style={{ marginBottom: '8px' }} aria-label="breadcrumb">
          <Link to="/releases">{t('Releases.Breadcrumb')}</Link>
          <p>{release ? `"${t(release.titleLang)}"` : t('Releases.PublishReleaseNotes')}</p>
        </Breadcrumbs>

        {releaseQuery.loading ? (
          <Box
            display="flex"
            marginTop="xl"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="small">
            <Loader color="var(--colorBlack)" />
            <Paragraph level={3}>{t('Releases.ReleaseNoteForm.Loading')}</Paragraph>
          </Box>
        ) : (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Heading level={1}>
                  {release
                    ? `${t('General.Edit')} ${t(release.titleLang)}`
                    : t('Releases.ReleaseNoteForm.PublishNew')}
                </Heading>
                {!release && <Paragraph level={3}>{t('Releases.ReleaseNoteForm.Desc')}</Paragraph>}
              </Box>

              {release && (
                <DeleteWrapper onClick={confirmDeleteModal.toggle}>
                  <Delete fontSize="large" htmlColor="var(--colorBlueGray900)" />
                </DeleteWrapper>
              )}
            </Box>

            <form onSubmit={handleSubmit}>
              <Box display="flex" flexDirection="column" gap="medium">
                <Field label={t('Releases.ReleaseNoteForm.Repo')}>
                  <Input
                    type="text"
                    required
                    value={formData.titleLang.en}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        titleLang: {
                          ...formData.titleLang,
                          en: e.target.value,
                        },
                      })
                    }
                    placeholder={t('Releases.ReleaseNoteForm.Repo.Placeholder')}
                  />
                </Field>

                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  gap="small">
                  {formData.notes.map((note, index) => (
                    <ReleaseNoteWrapper
                      key={index}
                      display="flex"
                      justifyContent="center"
                      alignItems="center">
                      <Field label={`${t('Releases.ReleaseNoteForm.Note')} #${index + 1}`}>
                        <Box display="flex" gap="small" alignItems="center">
                          <Input
                            type="text"
                            required
                            value={note.titleLang.en}
                            onChange={(e) => {
                              const updatedNotes = [...formData.notes];
                              updatedNotes[index] = {
                                ...updatedNotes[index],
                                titleLang: {
                                  ...updatedNotes[index].titleLang,
                                  en: e.target.value,
                                },
                              };

                              setFormData({
                                ...formData,
                                notes: updatedNotes,
                              });
                            }}
                            placeholder={t('Releases.ReleaseNoteForm.Note.Placeholder')}
                            style={{ flexGrow: 1 }}
                          />
                          <DeleteWrapper
                            onClick={() => {
                              const updatedNotes = [...formData.notes];
                              updatedNotes.splice(index, 1);

                              setFormData({
                                ...formData,
                                notes: updatedNotes,
                              });
                            }}>
                            <Delete fontSize="default" htmlColor="var(--colorBlueGray900)" />
                          </DeleteWrapper>
                        </Box>

                        <Box marginTop="small">
                          <Field
                            label={t('Releases.FeatureForm.ImageUrls')}
                            description={t('Releases.FeatureForm.ImageUrls.Desc')}>
                            {/* Render existing images */}
                            {note.imageUrls && note.imageUrls.length > 0 && (
                              <Box display="flex" gap="xs" marginBottom="small">
                                {note.imageUrls.map((url, imgIndex) => (
                                  <Box
                                    key={`image-${index}-${imgIndex}`}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    gap="xs">
                                    <img
                                      src={url}
                                      alt={`Uploaded ${imgIndex}`}
                                      style={{ height: '50px' }}
                                    />
                                    <Button
                                      styleType="Warning"
                                      height="24px"
                                      width="80px"
                                      text={t('General.Remove')}
                                      type="button"
                                      handleClick={() => {
                                        setFormData({
                                          ...formData,
                                          notes: formData.notes.map((n, i) =>
                                            i === index
                                              ? {
                                                  ...n,
                                                  imageUrls: (n.imageUrls || []).filter(
                                                    (_, ii) => ii !== imgIndex,
                                                  ),
                                                }
                                              : n,
                                          ),
                                        });
                                      }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {/* Always have one empty ImageSelect for adding new images */}
                            <ImageSelect
                              key={`imageUrl-${index}-${
                                note.imageUrls ? note.imageUrls.length : 0
                              }`}
                              name={`imageUrl-${index}-${
                                note.imageUrls ? note.imageUrls.length : 0
                              }`}
                              title={t('Releases.FeatureForm.ImageUrls.Add')}
                              setImgIdForAdding={(name, value, url) => {
                                if (url) {
                                  setFormData({
                                    ...formData,
                                    notes: formData.notes.map((n, i) =>
                                      i === index
                                        ? { ...n, imageUrls: [...(n.imageUrls || []), url] }
                                        : n,
                                    ),
                                  });
                                }
                              }}
                            />
                          </Field>

                          <Toggle
                            value={note.showToCustomers}
                            handleChange={(val) => {
                              const updatedNotes = [...formData.notes];
                              updatedNotes[index].showToCustomers = val;
                              setFormData({
                                ...formData,
                                notes: updatedNotes,
                              });
                            }}
                            title={t('Releases.ReleaseNoteForm.ShowToCustomers')}
                          />
                        </Box>
                      </Field>
                    </ReleaseNoteWrapper>
                  ))}
                </Box>

                <Box marginTop="small" display="flex" justifyContent="center">
                  <Button
                    text="Add note"
                    styleType="Dark"
                    width="80px"
                    type="button"
                    handleClick={addNote}
                  />
                </Box>

                <Box display="flex" flexDirection="column" gap="xs" marginTop="medium">
                  <Button
                    type="submit"
                    text={
                      loading
                        ? `${t('General.Loading')}...`
                        : release
                        ? t('General.Update')
                        : t('Releases.Publish')
                    }
                    styleType="OK"
                    disabled={loading || formData.notes.length === 0}
                  />
                </Box>
              </Box>
            </form>
          </Box>
        )}
      </Wrapper>

      <Modal isShowing={confirmDeleteModal.isShowing} hide={confirmDeleteModal.hide}>
        <ModalContentWrapper>
          <Box display="flex" flexDirection="column" gap="large">
            <Heading level={3}>{t('Releases.FeatureForm.ConfirmDelete')}</Heading>

            <Box display="flex" alignItems="center" gap="xs">
              <Button
                styleType="Dark"
                text={t('General.Cancel')}
                handleClick={confirmDeleteModal.hide}
              />
              <Button styleType="Warning" text={t('General.Delete')} handleClick={handleDelete} />
            </Box>
          </Box>
        </ModalContentWrapper>
      </Modal>
    </>
  );
};

export default ReleaseNoteForm;
